import {MbscCalendarEvent} from "@mobiscroll/react"

/*
   Timeline lane assignment.

   Mobiscroll stacks time-overlapping timeline events into lanes on its own, but it counts two
   things as an overlap that are not one for us:
     - buffer / preparation time, which makes two back-to-back reservations collide, and
     - the transparent block-period overlays, which sit exactly on those buffer windows.
   Both cases pushed ordinary reservations into half-height lanes, so the calendar used to pin
   every timeline event to the full row height in CSS. That hid the real case: two reservations
   genuinely running at the same time on one resource (a spa area with capacity > 1) were drawn
   exactly on top of each other and the lower one was invisible and unclickable.

   So we compute the lanes ourselves from the booked times only and let the CSS position them,
   see the `calendar-lane` rules in ReservationTimelineTab.css.
*/

/** Marker class; the lane CSS only applies to events that carry it. */
const LANE_CLASS = 'calendar-lane'

/**
 * Lanes past this are collapsed onto the last one. Four lanes already leave only ~40px per
 * reservation; splitting further would grow the rows past what is readable on screen.
 */
export const MAX_LANES = 4

/**
 * Height one lane needs to stay readable. The timeline runs with rowHeight:'equal', so all rows
 * grow together — which is why this is deliberately just under half the default row height: the
 * common case of two overlapping reservations then costs no extra height at all.
 */
export const LANE_HEIGHT_PX = 40
export const DEFAULT_ROW_HEIGHT_PX = 80

interface LaneAssignment {
    lane: number
    lanes: number
}

const toTime = (value: MbscCalendarEvent['start']): number => {
    if (value instanceof Date) return value.getTime()
    if (typeof value === 'number') return value
    if (typeof value === 'string') return new Date(value).getTime()
    return NaN
}

/** Timeline events always carry a single resource; an array can only come from external data. */
const resourceKey = (event: MbscCalendarEvent): string => {
    const resource = event.resource
    if (Array.isArray(resource)) return String(resource[0] ?? '')
    return String(resource ?? '')
}

/**
 * Greedy interval colouring, one resource at a time. Events are grouped into clusters of
 * transitively overlapping events so that a lone reservation next to a busy cluster still gets
 * the full row height instead of inheriting the cluster's lane count.
 */
const assignLanes = (
    entries: {index: number, start: number, end: number}[],
    assignments: Map<number, LaneAssignment>
) => {
    const ordered = [...entries].sort((a, b) => (a.start - b.start) || (a.end - b.end))

    let cluster: {index: number, lane: number}[] = []
    let laneEnds: number[] = []
    let clusterEnd = -Infinity

    const closeCluster = () => {
        if (!cluster.length) return

        const lanes = Math.min(laneEnds.length, MAX_LANES)
        cluster.forEach(({index, lane}) => {
            assignments.set(index, {lane: Math.min(lane, lanes - 1), lanes})
        })

        cluster = []
        laneEnds = []
        clusterEnd = -Infinity
    }

    ordered.forEach(entry => {
        // A gap to everything seen so far starts a fresh cluster with its own lane count.
        if (entry.start >= clusterEnd) closeCluster()

        let lane = laneEnds.findIndex(end => end <= entry.start)
        if (lane === -1) {
            lane = laneEnds.length
            laneEnds.push(entry.end)
        } else {
            laneEnds[lane] = entry.end
        }

        cluster.push({index: entry.index, lane})
        clusterEnd = Math.max(clusterEnd, entry.end)
    })

    closeCluster()
}

/**
 * Returns the events with their lane classes attached, plus the highest lane count on the page
 * so the caller can give the rows enough height for it.
 */
export const withTimelineEventLanes = (
    events: MbscCalendarEvent[]
): {events: MbscCalendarEvent[], laneCount: number} => {
    const byResource = new Map<string, {index: number, start: number, end: number}[]>()

    events.forEach((event, index) => {
        // Block periods are background overlays, not bookings — they must not consume a lane.
        if (event.isBlockPeriod) return

        const start = toTime(event.start)
        const end = toTime(event.end)
        if (!isFinite(start) || !isFinite(end)) return

        const key = resourceKey(event)
        const entries = byResource.get(key)
        if (entries) {
            entries.push({index, start, end})
        } else {
            byResource.set(key, [{index, start, end}])
        }
    })

    const assignments = new Map<number, LaneAssignment>()
    byResource.forEach(entries => assignLanes(entries, assignments))

    let laneCount = 1
    assignments.forEach(({lanes}) => {
        if (lanes > laneCount) laneCount = lanes
    })

    return {
        events: events.map((event, index) => {
            const assignment = assignments.get(index)
            if (!assignment) return event

            const laneClasses = [
                LANE_CLASS,
                `${LANE_CLASS}--i${assignment.lane}`,
                `${LANE_CLASS}--n${assignment.lanes}`
            ].join(' ')

            return {
                ...event,
                cssClass: [event.cssClass, laneClasses].filter(Boolean).join(' ')
            }
        }),
        laneCount
    }
}

/** Row height that fits `laneCount` lanes, never shrinking below the default row height. */
export const timelineRowHeight = (laneCount: number): number =>
    Math.max(DEFAULT_ROW_HEIGHT_PX, laneCount * LANE_HEIGHT_PX)
